import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PortalShell } from "@/components/portal/PortalShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  runReferenceSuite,
  listReferenceTestRuns,
  listSubsidyValidationTasks,
} from "@/lib/reference-suite.functions";

export const Route = createFileRoute("/_authenticated/app/validation")({
  component: ValidationPage,
});

function ValidationPage() {
  const qc = useQueryClient();
  const listRuns = useServerFn(listReferenceTestRuns);
  const listTasks = useServerFn(listSubsidyValidationTasks);
  const run = useServerFn(runReferenceSuite);

  const runs = useQuery({ queryKey: ["validation", "runs"], queryFn: () => listRuns() });
  const tasks = useQuery({ queryKey: ["validation", "tasks"], queryFn: () => listTasks() });

  const runM = useMutation({
    mutationFn: () => run(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["validation", "runs"] }),
  });

  return (
    <PortalShell nav={[
      { to: "/app/admin", label: "Admin" },
      { to: "/app/coverage", label: "Coverage" },
      { to: "/app/validation", label: "Validierung" },
    ]}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Validierung</h1>
          <p className="text-sm text-muted-foreground">Phase 11 – Förder-Expertenfreigaben & Referenz-Test-Suite</p>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Referenz-Test-Suite (10 Szenarien)</CardTitle>
            <Button size="sm" onClick={() => runM.mutate()} disabled={runM.isPending}>
              {runM.isPending ? "Läuft…" : "Suite starten"}
            </Button>
          </CardHeader>
          <CardContent>
            {(runs.data?.runs ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch keine Läufe.</p>
            ) : (
              <div className="space-y-2">
                {(runs.data?.runs ?? []).slice(0, 20).map((r) => (
                  <div key={r.id} className="flex items-start justify-between border rounded p-2 text-sm">
                    <div>
                      <div className="font-medium">{r.scenario_label}</div>
                      <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("de-DE")}</div>
                      {Array.isArray(r.deviations) && r.deviations.length > 0 && (
                        <ul className="list-disc pl-4 mt-1 text-xs">
                          {(r.deviations as string[]).map((d, i) => <li key={i}>{d}</li>)}
                        </ul>
                      )}
                    </div>
                    <Badge variant={r.status === "passed" ? "default" : "destructive"}>{r.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Förder-Validierungsaufgaben (Trust C → B)</CardTitle>
          </CardHeader>
          <CardContent>
            {(tasks.data?.tasks ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Keine offenen Aufgaben.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr><th className="py-1">Programm</th><th>Status</th><th>Quelle</th><th>Stand</th></tr>
                </thead>
                <tbody>
                  {(tasks.data?.tasks ?? []).map((t) => (
                    <tr key={t.id} className="border-t">
                      <td className="py-1 font-medium">{t.program_code}</td>
                      <td><Badge variant={t.status === "verified" ? "default" : "secondary"}>{t.status}</Badge></td>
                      <td className="text-xs"><a href={t.source_url ?? "#"} target="_blank" rel="noreferrer" className="underline">{t.source_url ? "Richtlinie" : "—"}</a></td>
                      <td className="text-xs">{t.source_as_of ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="text-xs text-muted-foreground mt-3">
              Aufgaben werden vom Förderexperten gepflegt. Nach Eintrag verifizierter Werte +
              Quelle wird das Programm auf Trust B angehoben und für Versand freigegeben.
            </p>
          </CardContent>
        </Card>
      </div>
    </PortalShell>
  );
}
